import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { elementScrollIntoView } from "seamless-scroll-polyfill";
import { css } from "@nju/css";
import { portalRoutes } from "@nju/routes";
import { A } from "@nju/ui";
import { useIsomorphicLayoutEffect } from "@nju/utils";
import { dashboardPaymentSubmenuMachine } from "./dashboardPaymentMachine";

interface IDashboardPaymentSubmenu {
  hideInvoicesOnNoEntries?: boolean;
}

export function DashboardPaymentSubmenu({
  hideInvoicesOnNoEntries = true,
}: IDashboardPaymentSubmenu) {
  const { pathname, query } = useRouter();
  const params = portalRoutes.mojeKonto.platnosci.index.input(query).unwrap();

  const mainPaymentUrl = portalRoutes.mojeKonto.platnosci.index.path();
  const historyUrl = portalRoutes.mojeKonto.platnosci.historia.path();
  const invoiceUrl = portalRoutes.mojeKonto.platnosci.faktury.path();

  const [current] = useMachine(dashboardPaymentSubmenuMachine, {
    context: { hideInvoicesOnNoEntries },
  });

  const { hasInvoices } = current.context;
  const ref = React.useRef<HTMLDivElement>(null);

  const [anchorRef, setAnchorRef] = React.useState<
    HTMLAnchorElement | undefined
  >(undefined);

  const invoiceRef = (ref: HTMLAnchorElement) => {
    if (ref) setAnchorRef(ref);
  };

  useIsomorphicLayoutEffect(() => {
    if (ref.current && anchorRef && pathname === invoiceUrl) {
      elementScrollIntoView(anchorRef, {
        inline: "start",
        behavior: "auto",
        block: "nearest",
      });
    }
  }, [pathname, anchorRef]);

  return (
    <div className={submenu()} ref={ref}>
      <Link
        href={portalRoutes.mojeKonto.platnosci.index.get({
          ...(params?.userId && {
            userId: params.userId,
          }),
          ...(params?.mode && {
            mode: params.mode,
          }),
          ...(params?.zmianaMetodyPlatnosci && {
            zmianaMetodyPlatnosci: params.zmianaMetodyPlatnosci,
          }),
        })}
        passHref={true}
      >
        <A
          data-cy="payments-method-switcher-button"
          display={{ "@initial": "flex", "@fromTablet": "inlineBlock" }}
          size="s"
          variant={pathname === mainPaymentUrl ? "accent" : "simple"}
        >
          metody płatności
        </A>
      </Link>
      <Link
        href={portalRoutes.mojeKonto.platnosci.historia.get({
          ...(params?.userId && { userId: params.userId }),
          ...(params?.mode && { mode: params.mode }),
          ...(params?.zmianaMetodyPlatnosci && {
            zmianaMetodyPlatnosci: params.zmianaMetodyPlatnosci,
          }),
        })}
        passHref={true}
      >
        <A
          data-cy="payments-history-switcher-button"
          display={{ "@initial": "flex", "@fromTablet": "inlineBlock" }}
          size="s"
          variant={pathname === historyUrl ? "accent" : "simple"}
        >
          historia płatności
        </A>
      </Link>
      {hasInvoices && (
        <Link
          href={portalRoutes.mojeKonto.platnosci.faktury.get({
            ...(params?.userId && {
              userId: params.userId,
            }),
            ...(params?.mode && { mode: params.mode }),
            ...(params?.zmianaMetodyPlatnosci && {
              zmianaMetodyPlatnosci: params.zmianaMetodyPlatnosci,
            }),
          })}
          passHref={true}
        >
          <A
            display={{ "@initial": "flex", "@fromTablet": "inlineBlock" }}
            ref={invoiceRef}
            size="s"
            variant={pathname === invoiceUrl ? "accent" : "simple"}
          >
            faktury
          </A>
        </Link>
      )}
    </div>
  );
}

const submenu = css({
  marginBottom: "20px",
  position: "relative",
  scrollbarWidth: "none",
  "-ms-overflow-style": "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  "@onlyMobile": {
    marginBottom: "15px",
    width: "calc(100vw - 45px)",
    overflowY: "hidden",
    whiteSpace: "nowrap",
    height: "55px",
  },
  display: "flex",
  /**
   * To jest mocno zamknięty kawałek wiec takie selektory nic nie rozwalą.
   * Niestety ciężko jest mi zrobić poprawny wygląd na mobile bez dodawania
   * całego wiadra variantów do elementu.
   */
  a: {
    whiteSpace: "nowrap",
    "@onlyMobile": {
      flex: 1,
      paddingLeft: "$10",
      paddingRight: "$10",
      fontSize: "14px",
    },
  },
});
